// Opaque record ids and record hashes, usable in Node and in plain-HTTP iPad Safari
// (no crypto.randomUUID or crypto.subtle there; crypto.getRandomValues works).

import { recordLine } from "./serialize.ts";
import { type AnyRecord, ID_PREFIX, type IdRecordType, type RecordType } from "./types.ts";

const ALPHABET = "0123456789abcdefghjkmnpqrstvwxyz";

/** A kind prefix plus 12 random base32 characters, e.g. `c-k3v9q2c7xw1d`. */
export function newId(type: IdRecordType): string {
  const bytes = crypto.getRandomValues(new Uint8Array(12));
  let s = `${ID_PREFIX[type]}-`;
  for (const b of bytes) s += ALPHABET[b & 31];
  return s;
}

/** Short non-cryptographic hash (cyrb53, two 32-bit lanes) of a string, as 14 hex digits. */
export function hashString(s: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < s.length; i++) {
    const ch = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16).padStart(14, "0");
}

/** The `base` of an edit op: the hash of a record's canonical line, or null when the record doesn't exist. */
export function recordHash(type: RecordType, record: AnyRecord | undefined): string | null {
  return record === undefined ? null : `h:${hashString(recordLine(type, record))}`;
}
